import React, { useState, useEffect } from 'react';
import { Search, Download, Percent, TrendingUp, TrendingDown } from 'lucide-react';
import axios from 'axios';

const AttendanceReportPercentage = () => {
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/students/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = students.filter(student =>
    student.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.admission_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getAttendanceColor = (percentage) => {
    if (percentage >= 75) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getAttendanceBadge = (percentage) => {
    if (percentage >= 75) return 'bg-green-100 text-green-800';
    if (percentage >= 60) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance Report with %</h1>
          <p className="text-sm text-gray-500 mt-1">View attendance percentage for all students</p>
        </div>
        <button className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
          <Download className="h-4 w-4" />
          Export Report
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Above 75%</p>
              <p className="text-3xl font-bold text-green-600 mt-1">0</p>
            </div>
            <TrendingUp className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">60% - 75%</p>
              <p className="text-3xl font-bold text-yellow-600 mt-1">0</p>
            </div>
            <Percent className="h-12 w-12 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Below 60%</p>
              <p className="text-3xl font-bold text-red-600 mt-1">0</p>
            </div>
            <TrendingDown className="h-12 w-12 text-red-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Class Average</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">0%</p>
            </div>
            <Percent className="h-12 w-12 text-blue-500" />
          </div>
        </div>
      </div>

      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
        <p className="text-sm text-yellow-700">
          Students with attendance below 75% may not be eligible for exams as per institute policy.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or admission number..."
            className="pl-10 pr-4 py-2 w-full border rounded-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No.</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Present</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Absent</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Percentage</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
            ) : filteredStudents.length === 0 ? (
              <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500">No students found</td></tr>
            ) : (
              filteredStudents.slice(0, 20).map((student) => {
                const percentage = 0; // This would be calculated from actual attendance data
                return (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{student.full_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{student.admission_number}</td>
                    <td className="px-6 py-4 text-center text-sm text-green-600 font-medium">0</td>
                    <td className="px-6 py-4 text-center text-sm text-red-600 font-medium">0</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-900 font-medium">0</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`text-2xl font-bold ${getAttendanceColor(percentage)}`}>
                        {percentage}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAttendanceBadge(percentage)}`}>
                        {percentage >= 75 ? 'Good' : percentage >= 60 ? 'Warning' : 'Critical'}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceReportPercentage;